// @ts-nocheck
// ClarityHeader — the clarity-forward top of the problem detail page.
//
// Clarity is the whole point of Orbit, so this surfaces it first: a compact
// score ring, the verdict, Orbit's one-line read, the dimension bars that
// explain WHY the score is what it is, any compliance flags, and a decision
// strip whose CTA matches the gate verdict (draft a PRD / deep scoping is
// paid / one more thing / keep scoping). Branding: yellow (accent-500) +
// black + blue (brand-500). Read-only artifact; actions hand off to Orbit.
import React from 'react';
import { requestAgentBridgeUrl } from '../../services/api';
import {
  Sparkles, ShieldAlert, ArrowRight, FileText, Compass, Phone,
} from 'lucide-react';

// 0–1 → percent int.
const pct = (v: number): number => Math.max(0, Math.min(100, Math.round((v ?? 0) * 100)));

// The six clarity dimensions, in the order Orbit weighs them, with plain labels.
const DIMENSIONS: Array<{ key: string; label: string }> = [
  { key: 'real_problem',          label: 'Real problem' },
  { key: 'outcome',               label: 'Outcome defined' },
  { key: 'as_is',                 label: 'Current state' },
  { key: 'to_be',                 label: 'Desired state' },
  { key: 'systems',               label: 'Systems named' },
  { key: 'deployment_coherence',  label: 'Deployment focus' },
];

// Verdict → how the score ring + badge read. We keep the palette tight:
// blue (brand) = clear/go, yellow (accent) = needs work, gray = not yet.
const VERDICT_VIEW: Record<string, { label: string; tone: 'go' | 'warn' | 'idle' }> = {
  clear:         { label: 'Clear',          tone: 'go' },
  needs_scoping: { label: 'Needs scoping',  tone: 'warn' },
  not_a_problem: { label: 'Not clear yet',  tone: 'idle' },
};

const TONE = {
  go:   { ring: '#1d4ed8', chip: 'bg-brand-500 text-white',           soft: 'text-brand-600' },   // brand blue
  warn: { ring: '#f59e0b', chip: 'bg-accent-500 text-gray-900',       soft: 'text-amber-600' },   // accent yellow
  idle: { ring: '#9ca3af', chip: 'bg-gray-200 text-gray-700',         soft: 'text-gray-500' },
};

// Decision strip — gate verdict → headline + CTA(s). All CTAs hand off to the
// Orbit panel (which owns the real PRD / deep-scope / continue flows) so this
// page stays a read-only report.
const decisionFor = (verdict: string): {
  icon: any; headline: string; sub: string;
  primary?: { label: string }; secondary?: { label: string };
  tone: 'go' | 'warn' | 'idle';
} => {
  switch (verdict) {
    case 'draft_prd':
      return { icon: FileText, tone: 'go',
        headline: 'Ready to build', sub: 'Clear, scoped, and deployable — Orbit can draft the PRD now.',
        primary: { label: 'Draft the PRD' } };
    case 'need_deployment':
      return { icon: Compass, tone: 'warn',
        headline: 'Almost there', sub: 'Just need where it runs, who uses it, and what triggers it.',
        primary: { label: 'Continue with Orbit' } };
    case 'need_urgency':
      return { icon: Compass, tone: 'warn',
        headline: 'Almost there', sub: 'Clear and deployable — Orbit just needs to know when you need it live.',
        primary: { label: 'Continue with Orbit' } };
    case 'need_budget':
      return { icon: Compass, tone: 'warn',
        headline: 'Almost there', sub: 'A rough budget lets Orbit shape the options and cost.',
        primary: { label: 'Continue with Orbit' } };
    case 'solution_partner':
      return { icon: ShieldAlert, tone: 'warn',
        headline: 'This needs a proper scope', sub: 'Clarity isn’t there yet. Do it yourself with Orbit, or book a call with a human Solution Partner.',
        primary: { label: 'Do it yourself' }, secondary: { label: 'Book a call with a human Solution Partner' } };
    case 'tell_me_more':
    default:
      return { icon: Compass, tone: 'idle',
        headline: 'Let’s keep scoping', sub: 'Orbit needs a bit more to read this problem clearly.',
        primary: { label: 'Continue with Orbit' } };
  }
};

export const ClarityHeader: React.FC<{
  gate: any; problemId: string; prdReady?: boolean; projectId?: string | null;
}> = ({ gate, problemId, prdReady, projectId }) => {
  if (!gate) return null;
  const clarity = gate.clarity || {};
  const cVerdict = String(clarity.verdict || 'needs_scoping');
  const view = VERDICT_VIEW[cVerdict] || VERDICT_VIEW.needs_scoping;
  const tone = TONE[view.tone];
  const score = pct(clarity.clarity_score);
  const dims = clarity.dimensions || {};
  // Already drafted (status=PRD_READY) WINS over the gate verdict. The gate
  // still says draft_prd (clear+deployable), but the PRD exists — so don't
  // offer to draft it again; surface "PRD ready → open the workspace."
  const decision = prdReady
    ? {
        icon: FileText, tone: 'go',
        headline: 'PRD ready',
        sub: 'Your PRD is drafted — open the workspace to build it.',
        primary: { label: 'Open workspace' },
      }
    : decisionFor(String(gate.verdict || ''));
  const decTone = TONE[decision.tone];
  const DIcon = decision.icon;

  const frameworks: string[] = [
    ...(gate.regulation?.inferred_frameworks || clarity.inferred_frameworks || []),
  ].filter((f) => f && f !== 'pii');
  const heavy = !!gate.regulation?.heavy;

  const openOrbit = () => window.dispatchEvent(new CustomEvent('orbit:open', {
    detail: { route: 'problem', context: `problem:${problemId}`, fresh: false },
  }));
  // When the PRD is already drafted, the primary CTA opens the build workspace
  // (the problem's own validx project); falls back to Orbit if we don't have
  // the project id for some reason.
  // The workspace lives on validx — open it via the HMAC bridge (new tab).
  // The in-app /agents/[id] viewer was retired; fall back to Orbit when we
  // have no project id or the bridge fails.
  const openWorkspace = async () => {
    if (!projectId) return openOrbit();
    try {
      const cid = localStorage.getItem('client_id') || '';
      const redirect =
        `/customer/agent/${projectId}` +
        `?clientId=${cid}&view=plan`;
      const data = await requestAgentBridgeUrl(redirect, cid || undefined);
      if (data?.url) { window.open(data.url, '_blank', 'noopener,noreferrer'); return; }
    } catch (e) {
      console.warn('[clarity] workspace bridge failed', e);
    }
    openOrbit();
  };
  const onPrimary = prdReady ? openWorkspace : openOrbit;

  // Ring geometry (SVG donut).
  const R = 30, C = 2 * Math.PI * R;
  const dash = (score / 100) * C;

  return (
    <section className="rounded-3xl bg-white ring-1 ring-gray-200/70 shadow-sm overflow-hidden">
      {/* Top: ring + verdict + Orbit's read */}
      <div className="p-5 sm:p-6 flex flex-col sm:flex-row gap-5 sm:gap-6 sm:items-center">
        {/* Score ring */}
        <div className="relative shrink-0 w-[84px] h-[84px]">
          <svg viewBox="0 0 72 72" className="w-[84px] h-[84px] -rotate-90">
            <circle cx="36" cy="36" r={R} fill="none" stroke="#eef2f7" strokeWidth="7" />
            <circle cx="36" cy="36" r={R} fill="none" stroke={tone.ring} strokeWidth="7"
              strokeLinecap="round" strokeDasharray={`${dash} ${C}`} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[19px] font-bold text-gray-900 leading-none tabular-nums">{score}<span className="text-[11px] font-semibold text-gray-400">%</span></span>
            <span className="text-[8.5px] font-sans uppercase tracking-wider text-gray-400 mt-0.5">clarity</span>
          </div>
        </div>

        {/* Verdict + one-line */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${tone.chip}`}>
              <Sparkles className="w-3 h-3" /> {view.label}
            </span>
            {clarity.source && clarity.source !== 'model' && (
              <span className="text-[10px] font-sans uppercase tracking-wider text-gray-300">{clarity.source}</span>
            )}
          </div>
          {clarity.one_line && (
            <p className="text-[14.5px] text-gray-800 leading-snug mt-2 max-w-[640px]">{clarity.one_line}</p>
          )}
          {/* Compliance flags — named, only when they actually matter */}
          {(frameworks.length > 0 || heavy) && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
              <ShieldAlert className="w-3.5 h-3.5 text-amber-600" />
              {heavy && <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Sensitive data</span>}
              {frameworks.map((f) => (
                <span key={f} className="text-[10.5px] font-sans uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200/60">{f}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dimension bars — the WHY behind the score */}
      <div className="px-5 sm:px-6 pb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-7 gap-y-2.5">
          {DIMENSIONS.filter((d) => d.key in dims).map((d) => {
            const v = pct(dims[d.key]);
            return (
              <div key={d.key} className="flex items-center gap-3">
                <span className="text-[11.5px] text-gray-500 w-[104px] shrink-0">{d.label}</span>
                <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${v}%`, background: v >= 70 ? '#1d4ed8' : v >= 45 ? '#f59e0b' : '#cbd5e1' }} />
                </div>
                <span className="text-[10.5px] font-sans text-gray-400 tabular-nums w-7 text-right">{(dims[d.key] ?? 0).toFixed(1)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Decision strip — Orbit's call + the next step */}
      <div className="border-t border-gray-100 bg-brand-50/50 px-5 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-2.5 min-w-0 flex-1">
          <span className={`mt-0.5 ${decTone.soft}`}><DIcon className="w-4 h-4" /></span>
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold text-gray-900">{decision.headline}</div>
            <p className="text-[12.5px] text-gray-500 leading-snug mt-0.5">{decision.sub}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {decision.secondary && (
            <button type="button" onClick={openOrbit}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12.5px] font-semibold bg-white ring-1 ring-gray-200 text-gray-700 hover:ring-brand-300 transition-colors whitespace-nowrap">
              <Phone className="w-3.5 h-3.5" /> {decision.secondary.label}
            </button>
          )}
          {decision.primary && (
            <button type="button" onClick={onPrimary}
              className={`whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-semibold transition-colors ${decision.tone === 'go' ? 'bg-brand-500 text-white hover:bg-brand-600' : 'bg-accent-500 text-gray-900 hover:bg-accent-400'}`}>
              {decision.primary.label} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default ClarityHeader;
